/**
 * Subject Selection Component
 * 
 * Enhanced with iOS-style animations and mobile-optimized UI:
 * - Card spring animations for a more natural feel
 * - Interactive card feedback on touch
 * - Progress bar animations
 * - Modern iOS-inspired typography and layout
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../firebase/AuthContext';
import './SubjectSelection.css';
import { Link } from 'react-router-dom';

/**
 * Subject Selection Component
 * 
 * Provides a stylized interface for selecting course subjects
 * Features responsive iOS-style cards with animations
 * 
 * @param {Object} props - Component properties
 * @param {Function} props.onSubjectSelect - Callback when subject is selected
 * @returns {JSX.Element} The subject selection interface
 */
function SubjectSelection({ onSubjectSelect }) {
  // Auth context for checking login status
  const { currentUser } = useAuth();
  
  // Log authentication status for debugging
  console.log("SubjectSelection - Auth Status:", currentUser ? "User Logged In" : "No User");
  
  // Component state
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectionInProgress, setSelectionInProgress] = useState(false);
  const [showLoginPrompt] = useState(true);
  
  // Ref to track if component is mounted
  const isMounted = useRef(true);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  /**
   * Trigger the subject selection callback
   * Ensures the selection is only triggered once with some delay
   * 
   * @param {string} subject - The selected subject code
   */
  const triggerSubjectSelection = (subject) => {
    console.log(`Triggering subject selection for ${subject} (User: ${currentUser ? "Logged In" : "Not Logged In"})`);
    
    // Verify the callback exists before calling
    if (typeof onSubjectSelect !== 'function') {
      console.error("onSubjectSelect is not a function!", onSubjectSelect);
      return;
    }
    
    // CRITICAL FIX: Call the callback immediately without any setTimeout
    // to ensure it definitely gets executed
    if (isMounted.current) {
      console.log(`Calling onSubjectSelect(${subject}) immediately...`);
      try {
        // Direct call with no delay to prevent timing issues
        onSubjectSelect(subject);
      } catch (error) {
        console.error("Error in onSubjectSelect:", error);
      }
    }
  };
  
  /**
   * Handle subject selection with animation
   * Animates the card before triggering the selection
   * Uses defensive programming to prevent race conditions
   * 
   * @param {string} subject - Selected subject code
   */
  const handleSelectSubject = (subject) => {
    console.log("Subject selected:", subject);
    
    // Guard clause: Prevent multiple rapid selections
    if (selectionInProgress) {
      console.log("Selection already in progress, ignoring");
      return;
    }
    
    // Mark selection as in progress immediately
    setSelectionInProgress(true);
    
    // Apply selection animation
    setSelectedCard(subject);
    
    // Simulate iOS haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    
    // Call selection immediately with no delay to ensure it happens
    triggerSubjectSelection(subject);
    
    // Reset selection state after a short delay
    setTimeout(() => {
      if (isMounted.current) {
        setSelectionInProgress(false);
      }
    }, 500);
  };
  
  /**
   * Direct handler for the select button clicks
   * Bypasses card animation for more reliable selection
   * This is the CRITICAL function that must work for select buttons
   * 
   * @param {Event} e - Click event
   * @param {string} subject - Selected subject code
   */
  const handleButtonClick = (e, subject) => {
    // Stop event propagation to prevent double handling with card click
    e.stopPropagation();
    e.preventDefault(); // Prevent any default button behavior
    
    console.log("Select button clicked for:", subject, "User:", currentUser ? "Logged In" : "Not Logged In");
    
    // Added debug logs to track execution
    console.log("Button clicked state:", {
      selectionInProgress,
      subject,
      isMounted: isMounted.current,
      onSubjectSelectExists: typeof onSubjectSelect === 'function'
    });
    
    // Force the selection state to prevent duplicate triggers
    setSelectionInProgress(true);
    
    // CRITICAL FIX: Directly call onSubjectSelect without any conditions or setTimeout
    if (typeof onSubjectSelect === 'function') {
      console.log("Directly calling onSubjectSelect with subject:", subject);
      onSubjectSelect(subject);
    } else {
      console.error("onSubjectSelect is not a function or undefined");
    }
    
    // Apply selection style
    setSelectedCard(subject);
    
    // Simulate iOS haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    
    // Reset selection state after a short delay
    setTimeout(() => {
      if (isMounted.current) {
        setSelectionInProgress(false);
      }
    }, 300);
  };
  
  /**
   * Handles skip login action
   * Hides the login prompt to let user continue without logging in
   * 
   * @param {Event} e - Click event
   */

  
  return (
    <div className="subject-selection-container">
      <div className="subject-selection-header">
        <h1 className="ios-title">English Acquaintances Game</h1>
        <p className="intro-text">
          Select a subject to play the game. Test your knowledge of literary works
          by correctly identifying key attributes.
        </p>
      </div>

      <div className="subject-cards">
        {/* ENGL 241 Card */}
        <div 
          className={`subject-card ios-card ${selectedCard === '241' ? 'card-selected' : ''}`}
          onClick={() => handleSelectSubject('241')}
          role="button"
          tabIndex={0}
          aria-label="Select ENGL 241 course"
          data-subject="241"
        >
          <div className="subject-card-header">
            <h2>English 241</h2>
            <span className="period-badge ios-badge">Nitesh Sir</span>
          </div>
          
          <div className="subject-description">
            <p>
            History of Indian English Literature: Post-Independence Period
            </p>
          </div>
          
          <div className="card-footer">
            <button 
              className="select-button ios-button"
              onClick={(e) => handleButtonClick(e, '241')}
              aria-label="Select ENGL 241"
              data-subject="241"
              type="button"
              style={{
                pointerEvents: 'auto',
                zIndex: 100,
                position: 'relative',
                opacity: 1
              }}
            >
              <span className="button-text">Select</span>
              <span className="button-icon">→</span>
            </button>
          </div>
        </div>
        
        {/* ENGL 242 Card */}
        <div
          className={`subject-card ios-card ${selectedCard === '242' ? 'card-selected' : ''}`}
          onClick={() => handleSelectSubject('242')}
          role="button"
          tabIndex={0}
          aria-label="Select ENGL 242 course"
          data-subject="242"
        >
          <div className="subject-card-header">
            <h2>English 242</h2>
            <span className="period-badge ios-badge">Anurag Sir</span>
          </div>

          <div className="subject-description">
            <p>
            History of English Literature: 1660-1750
            </p>
          </div>

          <div className="card-footer">
            <button
              className="select-button ios-button"
              onClick={(e) => handleButtonClick(e, '242')}
              aria-label="Select ENGL 242"
              data-subject="242"
              type="button"
              style={{
                pointerEvents: 'auto',
                zIndex: 100,
                position: 'relative',
                opacity: 1
              }}
            >
              <span className="button-text">Select</span>
              <span className="button-icon">→</span>
            </button>
          </div>
        </div>

        {/* ENGL 351 Card */}
        <div
          className={`subject-card ios-card ${selectedCard === '351' ? 'card-selected' : ''}`}
          onClick={() => handleSelectSubject('351')}
          role="button"
          tabIndex={0}
          aria-label="Select ENGL 351 course"
          data-subject="351"
        >
          <div className="subject-card-header">
            <h2>English 351</h2>
            <span className="period-badge ios-badge">Nitesh Sir</span>
          </div>

          <div className="subject-description">
            <p>
            History of English Literature: 1798-1830
            </p>
          </div>

          <div className="card-footer">
            <button
              className="select-button ios-button"
              onClick={(e) => handleButtonClick(e, '351')}
              aria-label="Select ENGL 351"
              data-subject="351"
              type="button"
              style={{
                pointerEvents: 'auto',
                zIndex: 100,
                position: 'relative',
                opacity: 1
              }}
            >
              <span className="button-text">Select</span>
              <span className="button-icon">→</span>
            </button>
          </div>
        </div>

        {/* ENGL 361 Card */}
        <div
          className={`subject-card ios-card ${selectedCard === '361' ? 'card-selected' : ''}`}
          onClick={() => handleSelectSubject('361')}
          role="button"
          tabIndex={0}
          aria-label="Select ENGL 361 course"
          data-subject="361"
        >
          <div className="subject-card-header">
            <h2>English 361</h2>
            <span className="period-badge ios-badge">Nitesh Sir</span>
          </div>

          <div className="subject-description">
            <p>
            History of English Literature: 1832-1901
            </p>
          </div>

          <div className="card-footer">
            <button
              className="select-button ios-button"
              onClick={(e) => handleButtonClick(e, '361')}
              aria-label="Select ENGL 361"
              data-subject="361"
              type="button"
              style={{
                pointerEvents: 'auto',
                zIndex: 100,
                position: 'relative',
                opacity: 1
              }}
            >
              <span className="button-text">Select</span>
              <span className="button-icon">→</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Only show login prompt if user is not logged in AND showLoginPrompt is true */}
      {!currentUser && showLoginPrompt && (
        <div className="login-prompt ios-card">
          <div className="login-prompt-content">
            <h3 className="prompt-title">Create Your Account <span className="optional-tag">(Optional)</span></h3>
            <p className="prompt-text">Sign in with Google to unlock these benefits:</p>
            <ul className="benefits-list">
              <li><span className="benefit-icon">🔄</span> Save your progress across devices</li>
              <li><span className="benefit-icon">🏆</span> Compete with other students from your college</li>
              <li><span className="benefit-icon">📊</span> Track your mastery of literary works</li>
              <li><span className="benefit-icon">🥇</span> Appear on the leaderboard</li>
            </ul>
            <div className="login-buttons">
              <Link to="/login" className="prompt-button">
                <span className="button-icon">G</span>
                <span className="button-text">Sign in with Google</span>
              </Link>
 
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubjectSelection; 